/**
 * Platform / Auth-Kit identity for Knowledge Assistant.
 *
 * Primary path (ka.nationaldatacare.com, including Hub iframe): Node unseals
 * the shared HttpOnly `wos-session` cookie on `/api/me`. Identity
 * `GET /ecs/identity/workos/session-token` is a fallback when that cookie is
 * not yet visible. Login/logout stay on Auth-Kit.
 *
 * Fallback: Hub opener/parent postMessage (`ndc-platform-auth-v1`) if cookies
 * are not yet shared.
 *
 * Uses `X-NDC-Access-Token` (not Authorization Bearer) so Azure Easy Auth does
 * not reject a non-Entra token.
 */

export const PLATFORM_AUTH_READY_TYPE = "ndc-ka-ready-v1";
export const PLATFORM_AUTH_MESSAGE_TYPE = "ndc-platform-auth-v1";

/** Must match NDC-Auth-Kit CLIENT_AUTH_MARKER_NAME */
export const AUTH_KIT_MARKER_NAME = "ndc-authenticated";

const STORAGE_TOKEN_KEY = "ndc_platform_access_token";
const STORAGE_USER_KEY = "ndc_platform_user";

const DEFAULT_PLATFORM_ORIGINS = [
  "https://app-dev.nationaldatacare.com",
  "https://app.nationaldatacare.com",
  "http://localhost:3010",
  "http://127.0.0.1:3010",
];

const DEFAULT_AUTH_PROD = "https://auth.nationaldatacare.com";
const DEFAULT_AUTH_STAGING = "https://auth-staging.nationaldatacare.com";
const DEFAULT_AUTH_LOCAL = "http://localhost:3005";
const DEFAULT_IDENTITY_WORKOS =
  "https://api-dev.nationaldatacare.com/ecs/identity/workos";
const DEFAULT_LOCAL_IDENTITY = "http://localhost:8080/api/identity/workos";

export type PlatformUser = {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
};

export type PlatformSession = {
  accessToken: string;
  user: PlatformUser | null;
};

function allowedPlatformOrigins(): Set<string> {
  const fromEnv = (import.meta.env.VITE_PLATFORM_ORIGINS as string | undefined)
    ?.split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return new Set(fromEnv?.length ? fromEnv : DEFAULT_PLATFORM_ORIGINS);
}

export function isPlatformOrigin(origin: string): boolean {
  if (allowedPlatformOrigins().has(origin)) return true;
  try {
    const host = new URL(origin).hostname;
    return (
      host === "localhost" ||
      host === "127.0.0.1" ||
      host.endsWith(".nationaldatacare.com")
    );
  } catch {
    return false;
  }
}

function isTeamsAncestorOrigin(origin: string): boolean {
  return (
    origin.includes("teams.microsoft.com") ||
    origin.includes("teams.cloud.microsoft") ||
    origin.includes("skype.com")
  );
}

function isFramed(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

function parentLooksLikeTeams(): boolean {
  const params = new URLSearchParams(window.location.search);
  if (params.has("theme")) return true;
  try {
    const ancestors = window.location.ancestorOrigins;
    if (ancestors && ancestors.length > 0 && isTeamsAncestorOrigin(ancestors[0])) {
      return true;
    }
  } catch {
    /* Firefox has no ancestorOrigins */
  }
  return false;
}

/**
 * True when KA is inside Application Hub (iframe or `embed=1`).
 * Hub already shows NDC chrome and Sign out — KA hides its copy of those.
 * Any non-Teams iframe counts: Hub hosts vary (custom domain vs Azure SWA).
 */
export function isEmbeddedInHub(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get("embed") === "1" || params.get("from") === "platform") return true;
  if (!isFramed()) return false;
  if (parentLooksLikeTeams()) return false;
  return true;
}

/** True when Auth-Kit's client-readable session marker is on this host. */
export function hasAuthKitMarker(): boolean {
  if (typeof document === "undefined") return false;
  return new RegExp(`(?:^|;\\s*)${AUTH_KIT_MARKER_NAME}=`).test(document.cookie);
}

function stripSlash(url: string): string {
  return url.replace(/\/$/, "");
}

/**
 * Auth-Kit hosts for Sign in / Sign out only. Session-token is Identity.
 * ka.nationaldatacare.com does not encode staging vs prod. Hub opens KA with
 * noopener (no referrer), and `auth.nationaldatacare.com` does not resolve yet,
 * so the default is **staging** first. Override with `VITE_NDC_AUTH_BASE_URL`.
 */
export function authKitBaseCandidates(): string[] {
  const seen = new Set<string>();
  const add = (raw: string | undefined | null) => {
    if (!raw) return;
    const url = stripSlash(raw.trim());
    if (url && !seen.has(url)) seen.add(url);
  };

  add(import.meta.env.VITE_NDC_AUTH_BASE_URL as string | undefined);

  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") {
      add(DEFAULT_AUTH_LOCAL);
      return [...seen];
    }

    try {
      const ref = document.referrer || "";
      if (ref.includes("app.nationaldatacare.com") || ref.includes("://auth.nationaldatacare.com")) {
        add(DEFAULT_AUTH_PROD);
      } else if (ref.includes("app-dev.nationaldatacare.com") || ref.includes("auth-staging")) {
        add(DEFAULT_AUTH_STAGING);
      }
    } catch {
      /* ignore */
    }

    add(DEFAULT_AUTH_STAGING);
    add(DEFAULT_AUTH_PROD);
  }

  return [...seen];
}

/** Kong public Identity WorkOS routes. Never Auth Kit SWA /api/session-token. */
export function identityWorkosBaseUrl(): string {
  const fromEnv = (
    import.meta.env.VITE_IDENTITY_WORKOS_URL as string | undefined
  )?.trim();
  if (fromEnv) return stripSlash(fromEnv);
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") {
      return DEFAULT_LOCAL_IDENTITY;
    }
  }
  return DEFAULT_IDENTITY_WORKOS;
}

function parseSessionTokenPayload(data: unknown): PlatformSession | null {
  if (!data || typeof data !== "object") return null;
  const rec = data as Record<string, unknown>;
  if (typeof rec.accessToken !== "string" || !rec.accessToken.trim()) return null;
  return {
    accessToken: rec.accessToken.trim(),
    user: normalizeUser(rec.user),
  };
}

/**
 * Exchange the shared Auth-Kit cookie for a WorkOS access token via Identity.
 * The JS-readable marker is optional: `wos-session` is HttpOnly, so Hub
 * iframes can still send it with credentials when `ndc-authenticated` is
 * not visible to `document.cookie`.
 */
export async function fetchAuthKitSession(): Promise<PlatformSession | null> {
  const existing = readPlatformSession();
  if (existing?.accessToken) return existing;

  if (typeof window === "undefined") return null;

  try {
    const res = await fetch(`${identityWorkosBaseUrl()}/session-token`, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
      cache: "no-store",
    });
    if (!res.ok) return null;
    const session = parseSessionTokenPayload(await res.json());
    if (session) {
      storePlatformSession(session);
      return session;
    }
  } catch {
    /* Identity unreachable */
  }

  return null;
}

export function isPlatformLaunch(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get("from") === "platform") return true;
  // Hub iframe is cookie SSO, not postMessage. Platform does not post a token
  // into the iframe — waiting for parent would only delay Sign in.
  try {
    return Boolean(window.opener && !window.opener.closed);
  } catch {
    return false;
  }
}

export function readPlatformSession(): PlatformSession | null {
  try {
    const accessToken = sessionStorage.getItem(STORAGE_TOKEN_KEY);
    if (!accessToken) return null;
    const rawUser = sessionStorage.getItem(STORAGE_USER_KEY);
    let user: PlatformUser | null = null;
    if (rawUser) {
      const parsed = JSON.parse(rawUser) as PlatformUser;
      if (parsed && typeof parsed.id === "string" && typeof parsed.email === "string") {
        user = parsed;
      }
    }
    return { accessToken, user };
  } catch {
    return null;
  }
}

export function storePlatformSession(session: PlatformSession): void {
  sessionStorage.setItem(STORAGE_TOKEN_KEY, session.accessToken);
  if (session.user) {
    sessionStorage.setItem(STORAGE_USER_KEY, JSON.stringify(session.user));
  } else {
    sessionStorage.removeItem(STORAGE_USER_KEY);
  }
}

export function clearPlatformSession(): void {
  sessionStorage.removeItem(STORAGE_TOKEN_KEY);
  sessionStorage.removeItem(STORAGE_USER_KEY);
}

/**
 * True when the Chat-Bot backend accepted this Platform token (`/api/me`
 * has a user id). Clears local storage on failure so the UI cannot look
 * signed-in while chat still returns "Sign in required."
 */
export async function confirmBackendSession(
  session?: PlatformSession | null,
): Promise<boolean> {
  const active = session ?? readPlatformSession();
  if (!active?.accessToken) return false;
  try {
    const res = await fetch("/api/me", {
      headers: platformAuthHeaders(active),
      credentials: "include",
      cache: "no-store",
    });
    if (!res.ok) {
      clearPlatformSession();
      return false;
    }
    const data = (await res.json()) as { signedIn?: boolean; id?: string | null };
    if (data.signedIn && typeof data.id === "string" && data.id.trim()) return true;
    clearPlatformSession();
    return false;
  } catch {
    clearPlatformSession();
    return false;
  }
}

/**
 * Hub iframe path: KA and Hub share `wos-session` on `.nationaldatacare.com`.
 * The Node app unseals that cookie on `/api/me` even when Identity never
 * returned an access token to sessionStorage.
 */
export async function confirmCookieBackendSession(): Promise<boolean> {
  try {
    const res = await fetch("/api/me", {
      credentials: "include",
      cache: "no-store",
    });
    if (!res.ok) return false;
    const data = (await res.json()) as { signedIn?: boolean; id?: string | null };
    return Boolean(data.signedIn && typeof data.id === "string" && data.id.trim());
  } catch {
    return false;
  }
}

function normalizeUser(raw: unknown): PlatformUser | null {
  if (!raw || typeof raw !== "object") return null;
  const u = raw as Record<string, unknown>;
  if (typeof u.id !== "string" || typeof u.email !== "string") return null;
  return {
    id: u.id,
    email: u.email,
    firstName: typeof u.firstName === "string" ? u.firstName : null,
    lastName: typeof u.lastName === "string" ? u.lastName : null,
  };
}

/**
 * Wait for Platform opener to post the Auth-Kit access token.
 * Resolves null on timeout / missing opener.
 */
export function awaitPlatformAuthHandoff(timeoutMs = 8000): Promise<PlatformSession | null> {
  const existing = readPlatformSession();
  if (existing) return Promise.resolve(existing);

  if (typeof window === "undefined") return Promise.resolve(null);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (session: PlatformSession | null) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", onMessage);
      window.clearTimeout(timer);
      resolve(session);
    };

    const onMessage = (event: MessageEvent) => {
      if (!isPlatformOrigin(event.origin)) return;
      const data = event.data as { type?: string; accessToken?: string; user?: unknown };
      if (!data || data.type !== PLATFORM_AUTH_MESSAGE_TYPE) return;
      if (typeof data.accessToken !== "string" || !data.accessToken.trim()) return;
      const session: PlatformSession = {
        accessToken: data.accessToken.trim(),
        user: normalizeUser(data.user),
      };
      storePlatformSession(session);
      finish(session);
    };

    window.addEventListener("message", onMessage);

    try {
      const payload = { type: PLATFORM_AUTH_READY_TYPE };
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(payload, "*");
      }
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(payload, "*");
      }
    } catch {
      // Cross-origin opener/parent blocked — handoff will time out.
    }

    const timer = window.setTimeout(() => finish(readPlatformSession()), timeoutMs);
  });
}

function primaryAuthKitBase(): string {
  return authKitBaseCandidates()[0] || DEFAULT_AUTH_STAGING;
}

export function isNationalDatacareHost(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "nationaldatacare.com" || host.endsWith(".nationaldatacare.com");
}

function hubKnowledgeAssistantUrl(): string {
  try {
    const ancestors = window.location.ancestorOrigins;
    if (ancestors && ancestors.length > 0) {
      const origin = ancestors[0];
      if (isPlatformOrigin(origin)) {
        return `${stripSlash(origin)}/knowledgeassistant`;
      }
    }
  } catch {
    /* Safari / older browsers */
  }
  try {
    const ref = document.referrer || "";
    if (ref) {
      const origin = new URL(ref).origin;
      if (isPlatformOrigin(origin)) {
        return `${stripSlash(origin)}/knowledgeassistant`;
      }
    }
  } catch {
    /* ignore */
  }
  return "https://app-dev.nationaldatacare.com/knowledgeassistant";
}

/** Auth-Kit or Easy Auth sign-in URL for this host. */
export function signInHref(): string {
  if (typeof window === "undefined") return "/.auth/login/aad";
  const returnTo = isEmbeddedInHub()
    ? hubKnowledgeAssistantUrl()
    : `${window.location.origin}/`;
  if (isNationalDatacareHost()) {
    return `${primaryAuthKitBase()}/login?return_to=${encodeURIComponent(returnTo)}`;
  }
  return `/.auth/login/aad?post_login_redirect_url=${encodeURIComponent(
    window.location.pathname + window.location.search,
  )}`;
}

/**
 * Start Auth-Kit / Easy Auth sign-in. From the Hub iframe this must navigate
 * the top window — Auth-Kit cannot be framed, so iframe `location` looks dead.
 */
export function beginSignIn(): void {
  if (typeof window === "undefined") return;
  const href = signInHref();
  if (isEmbeddedInHub()) {
    try {
      if (window.top) {
        window.top.location.href = href;
        return;
      }
    } catch {
      /* sandbox without allow-top-navigation */
    }
  }
  window.location.href = href;
}

/**
 * Clear local Platform token and leave via Auth-Kit or Easy Auth logout.
 * On ka.nationaldatacare.com this clears the shared wos-session cookie.
 */
export function signOut(): void {
  const useAuthKit =
    hasAuthKitMarker() ||
    Boolean(readPlatformSession()?.accessToken) ||
    isNationalDatacareHost();
  clearPlatformSession();
  if (typeof window === "undefined") return;
  const returnTo = `${window.location.origin}/`;
  if (useAuthKit) {
    window.location.href = `${primaryAuthKitBase()}/logout?return_to=${encodeURIComponent(returnTo)}`;
    return;
  }
  window.location.href = `/.auth/logout?post_logout_redirect_uri=${encodeURIComponent(returnTo)}`;
}

/** Headers for Chat-Bot API calls when a Platform WorkOS session is present. */
export function platformAuthHeaders(
  session?: PlatformSession | null,
): Record<string, string> {
  const active = session ?? readPlatformSession();
  if (!active?.accessToken) return {};
  const headers: Record<string, string> = {
    "X-NDC-Access-Token": active.accessToken,
  };
  if (active.user) {
    headers["X-NDC-Platform-User"] = JSON.stringify({
      id: active.user.id,
      email: active.user.email,
      firstName: active.user.firstName ?? null,
      lastName: active.user.lastName ?? null,
    });
  }
  return headers;
}
